'use client';
import useVaultCollection from '@/application/read/collections/useVaultCollection';
import Collection, { CollectionTab } from './Collection';
import { useNetwork } from '@/presentation/context/network';
import Marketplace from './Marketplace';
import YourItems from './YourItems';
import Info from './Info';
import { useRouter } from 'next/navigation';
import useCollection from '@/application/read/collections/useCollection';
import useVault from '@/application/read/vaults/useVault';
import { getCollectionPath } from '@/presentation/utils/paths';
import { useModalContext } from '@/presentation/context/modal';
import { CreatePositionModalProps } from '@/presentation/context/modal/ModalContext';

export interface CollectionContainerProps {
  slug: string;
  vaultSlug?: string;
  tab: CollectionTab;
}
const CollectionContainer = ({
  slug: collectionSlug,
  vaultSlug,
  tab,
}: CollectionContainerProps) => {
  const router = useRouter();
  const { chain, slug: chainSlug } = useNetwork();
  const { data: collection } = useVaultCollection(collectionSlug);
  const collectionAddress = collection?.collectionAddress;
  const { data: metadata } = useCollection(collectionAddress);
  const { data: selectedVault } = useVault({ slug: vaultSlug });
  const { showCreatePositionModal, showSwapModal } = useModalContext();

  const makePath = (
    tab: CollectionTab,
    vault: { slug: string } | undefined
  ) => {
    return getCollectionPath({
      chain: chainSlug,
      collectionSlug,
      tab,
      vaultSlug: vault?.slug,
    });
  };

  const onSelectVault = (vault: { slug: string } | undefined) => {
    router.replace(makePath(tab, vault));
  };

  const onAddToPool = ({ vaultId, ...rest }: CreatePositionModalProps) =>
    showCreatePositionModal({
      isNewVault: false,
      vaultId,
      ...rest,
    });

  const onTabChange = (tab: CollectionTab) => {
    router.replace(makePath(tab, selectedVault));
  };

  if (!collection || !collectionAddress) {
    return null;
  }

  return (
    <Collection
      {...collection}
      {...metadata}
      chain={chain}
      onAddToPool={onAddToPool}
      onBuyToken={() =>
        showSwapModal({
          buyVaultId: selectedVault?.vaultId || collection.vaults[0].vaultId,
          buyVaultAddress: selectedVault?.id || collection.vaults[0].id,
          buyToken: selectedVault?.id || collection.vaults[0].id,
          buyAmount: `${1}`,
          sellType: 'ETH',
          buyType: 'vTOKEN',
        })
      }
      tabs={[
        <Marketplace
          key="buy"
          vaults={collection.vaults}
          onSelectVault={onSelectVault}
          selectedVault={selectedVault}
          collectionAddress={collectionAddress}
        />,
        <YourItems
          key="wallet"
          collectionAddress={collectionAddress}
          vaults={collection?.vaults}
          selectedVault={selectedVault}
        />,
        <Info
          key="info"
          assetAddress={collectionAddress}
          vaults={collection?.vaults}
          onSelectVault={onSelectVault}
          selectedVault={selectedVault}
        />,
      ]}
      onSelectVault={onSelectVault}
      selectedVault={selectedVault}
      tab={tab}
      onTabChange={onTabChange}
    />
  );
};

export default CollectionContainer;
